'use client';

import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Background,
  ReactFlow,
  addEdge,
  ConnectionLineType,
  Panel,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  PanOnScrollMode,
  NodeProps,
  NodeTypes,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

// import { layoutElements } from './layout-elements';
import { layoutElements } from './layout';
import SideQuestNode from './nodes/SideQuestNode';
import StartNode from './nodes/StartNode';
import EndNode from './nodes/EndNode';
import PluralisticNode from './nodes/PluralisticNode';
import SubtopicNode from './nodes/SectionNode';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import LoadingSpinnerSmall from '../global/loading/LoadingSpinnerSmall';
import {
  EntitreeMap,
  ExtraTopic,
  Pass1LearningPath,
  Pass2LearningPath,
} from '../../typesV2';
import { convertLearningPathToEntitreeFormat } from '../../lib/graph/learningPathConverter';
import { updateEntitreeWithEmbellishedData } from '../../lib/graph/learningPathConverter';
import { isStreamingMapAtom } from '../../jotaiStore';
import { useAtom } from 'jotai';
import { contentAtom, pass2LearningPathAtom } from '../../jotaiStore';
import { pass1LearningPathAtom } from '../../jotaiStore';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useSetAtom } from 'jotai';
import { viewportAtom } from '../../jotaiStore';
import { ReactFlowInstructions } from './FlowScrollHelper';

// import StartNode from '../mapView/StartNode';

// const nodeTypes = {
//   custom: CustomNode,
// };

// Create adapter wrappers for each node component
const SideQuestNodeWrapper = (props: NodeProps) => <SideQuestNode {...(props as any)} />;
const PluralisticNodeWrapper = (props: NodeProps) => <PluralisticNode {...(props as any)} />;
const StartNodeWrapper = (props: NodeProps) => <StartNode {...(props as any)} />;
const EndNodeWrapper = (props: NodeProps) => <EndNode {...(props as any)} />;
const SubtopicNodeWrapper = (props: NodeProps) => <SubtopicNode {...(props as any)} />;

const nodeTypes: NodeTypes = {
  sideQuest: SideQuestNodeWrapper,
  pluralistic: PluralisticNodeWrapper,
  start: StartNodeWrapper,
  end: EndNodeWrapper,
  normal: SubtopicNodeWrapper,
};

// const { nodes: layoutedNodes, edges: layoutedEdges } = layoutElements(
//   initialTree2,
//   treeRootId,
//   'TB'
// );

const startingNode: ExtraTopic = {
  topicTitle: 'Overview',
  topicDescription: 'This is the overview to the learning path',
  type: 'start',
};

const endingNode: ExtraTopic = {
  topicTitle: "What's Next?",
  topicDescription: 'This is the conclusion to the learning path',
  type: 'end',
};

const { nodes: layoutedNodes, edges: layoutedEdges } = layoutElements(
  {
    1: startingNode,
  },
  // initialTree4,
  1,
  'TB'
);

const DEFAULT_X = 120;
const DEFAULT_Y = 50;
const GRAPH_NODE_PADDING = 100;
const GRAPH_ZOOM_RATIO = 760;
const GRAPH_ZOOM_RATIO_SMALL = 650;
interface LayoutFlowProps {
  graphID: string;
  containerWidth: number;
}

const getInitialGraphViewParams = (containerWidth: number) => {
  // const calculatedX = containerWidth * 0.265;
  let calculatedX;
  let calulatedZoom;

  if (containerWidth > 450) {
  calculatedX = containerWidth * 0.219 + 30.248;
  calulatedZoom =
      (containerWidth - GRAPH_NODE_PADDING) / GRAPH_ZOOM_RATIO;
  } else {
    calculatedX = containerWidth * 0.267;
    calulatedZoom =
      (containerWidth - GRAPH_NODE_PADDING) / GRAPH_ZOOM_RATIO_SMALL;
  }
  return { calculatedX, calulatedZoom };
};

const LayoutFlow = ({ graphID, containerWidth }: LayoutFlowProps) => {
  const { calculatedX, calulatedZoom } = getInitialGraphViewParams(
    containerWidth
  );

  const setViewport = useSetAtom(viewportAtom);

  const searchParams = useSearchParams();
  const graphX = searchParams.get('x')
    ? parseFloat(searchParams.get('x')!)
    : calculatedX;
  const graphY = searchParams.get('y')
    ? parseFloat(searchParams.get('y')!)
    : DEFAULT_Y;
  const graphZoom = searchParams.get('zoom')
    ? parseFloat(searchParams.get('zoom')!)
    : calulatedZoom;

  useEffect(() => {
    setViewport({
      x: graphX,
      y: graphY,
      zoom: graphZoom,
    });
  }, [graphX, graphY, graphZoom, setViewport]);

  // useEffect(() => {
  //   if (graphViewContainerRef.current) {
  //     setGraphViewWidth(graphViewContainerRef.current.clientWidth);
  //   }
  // }, [graphViewContainerRef]);

  // Add this effect to update zoom based on container width
  // useEffect(() => {
  //   if (containerWidth > 0) {
  //     // Calculate appropriate zoom level based on container width
  //     const zoomLevel = Math.min(1, containerWidth / 1200); // Adjust 1200 to your desired base width
  //     setViewport({ x: 0, y: 0, zoom: zoomLevel });
  //   }
  // }, [containerWidth]);

  // PASS 1
  const pass1LearningPath = useAtomValue(pass1LearningPathAtom);
  const [pass1Tree, setPass1Tree] = useState<EntitreeMap | null>(null);

  // PASS 2
  const pass2LearningPath = useAtomValue(pass2LearningPathAtom);
  const [pass2Tree, setPass2Tree] = useState<EntitreeMap | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  // Ref to store the debounce timeout ID - persists between renders without causing re-renders
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced viewport change handler - only updates the viewport state after the user stops
  // panning/zooming for 300ms. This prevents excessive state updates during continuous viewport changes.
  const onViewportChange = useCallback(
    (viewport) => {
      // Cancel any pending viewport updates
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule a new viewport update after 300ms of inactivity
      timeoutRef.current = setTimeout(() => {
        setViewport({
          x: viewport.x,
          y: viewport.y,
          zoom: viewport.zoom,
        });
      }, 300);
    },
    [setViewport]
  );

  // Clean up the timeout when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // PASS 1 UPDATES
  useEffect(() => {
    if (pass1LearningPath) {
      const learningPath = Array.isArray(pass1LearningPath)
        ? pass1LearningPath
        : [];
      const pass1PathWithIntro: Pass1LearningPath = [
        // @ts-ignore
        startingNode,
        // @ts-ignore
        ...(learningPath || []),
        // @ts-ignore
        endingNode,
      ];
      const pass1Tree = convertLearningPathToEntitreeFormat(pass1PathWithIntro);
      setPass1Tree(pass1Tree);

      // Generate new nodes and edges
      const { nodes: newNodes, edges: newEdges } = layoutElements(
        pass1Tree,
        1, // Assuming the root ID is always 1
        'TB'
      );

      // Update the nodes and edges
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [pass1LearningPath, setNodes, setEdges]);

  // PASS 2 UPDATES
  useEffect(() => {
    if (pass2LearningPath) {
      const pass2PathWithIntro: Pass2LearningPath = [
        // @ts-ignore
        startingNode,
        // @ts-ignore
        ...pass2LearningPath,
        // @ts-ignore
        endingNode,
      ];
      let convertedPass2Tree: EntitreeMap | null = null;
      if (!pass1Tree) {
        convertedPass2Tree =
          convertLearningPathToEntitreeFormat(pass2PathWithIntro);

        setPass1Tree(convertedPass2Tree);
      }
      if (pass1Tree || convertedPass2Tree) {
        const pass2Tree = updateEntitreeWithEmbellishedData(
          pass1Tree || (convertedPass2Tree as EntitreeMap),
          pass2PathWithIntro
        );
        setPass2Tree(pass2Tree);

        // Generate new nodes and edges
        const { nodes: newNodes, edges: newEdges } = layoutElements(
          pass2Tree,
          1, // Assuming the root ID is always 1
          'TB'
        );

        // Update the nodes and edges
        setNodes(newNodes);
        setEdges(newEdges);
      }
    }
  }, [pass2LearningPath, setNodes, setEdges]);

  // if (isStreamingMap) {
  //   return (
  //     <div className="flex justify-center mb-2 text-colors-green">
  //       <LoadingSpinnerSmall className="w-12 h-12" />
  //     </div>
  //   );
  // }

  if (nodes?.length === 0 && edges?.length === 0) {
    return <LoadingSpinnerSmall className="w-12 h-12" />;
  }

  if (graphZoom < 0) return null;

  return (
    <span className="w-full max-w-full @container/sidebar">
      <TooltipProvider>
        {/* {isStreamingMap && (
        <div className="flex justify-center mb-2 text-colors-green">
          <LoadingSpinnerSmall className="w-12 h-12" />
        </div>
      )} */}
        <ReactFlowInstructions />
        <ReactFlow
          key={'layoutFlow'}
          id={graphID}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onViewportChange={onViewportChange}
          connectionLineType={ConnectionLineType.SmoothStep}
          // fitView
          nodesDraggable={false}
          panOnScroll={true}
          panOnScrollMode={PanOnScrollMode.Vertical}
          panOnDrag={false}
          nodeTypes={nodeTypes}
          style={{ backgroundColor: '#2F2E38' }}
          // style={{ backgroundColor: '#26252D' }}
          minZoom={0.3}
          defaultViewport={{
            x: graphX,
            y: graphY,
            zoom: graphZoom,
          }}
        >
          <Background
            id={`${graphID}-background`}
            color="#ffffff"
            gap={61}
            variant={BackgroundVariant.Cross}
            size={10}
            style={{ opacity: 0.2 }}
          />
        </ReactFlow>
      </TooltipProvider>
    </span>
  );
};

export default LayoutFlow;
